import { CSSProperties, FC, useEffect, useState } from 'react';

import { Checkbox, Modal, Select, Snackbar, TextField } from '@atom/mui';
import {
  ChartExportFormat,
  VegaLiteSpec,
} from '@atom/types/conversationalAnalytics';
import api from '@atom/utilities/api';
import { getChannelTitle } from '@atom/utilities/conversationalAnalyticsUtilities';
import {
  CHART_EXPORT_ENDPOINT,
  FILES_ENDPOINT,
} from '@atom/utilities/endpoints';
import { downloadFile } from '@atom/utilities/fileUtilities';

import { useChatContext } from './ChatContext';

export type ChatExportChartProps = {
  spec: VegaLiteSpec;
  open: boolean;
  onClose: () => void;
};

type ChartExportConfig = {
  format: ChartExportFormat;
  title: string;
  xAxisTitle: string;
  yAxisTitle: string;
  width: number;
  height: number;
  includeValueLabels: boolean;
};

type ChartExportResponse = { fileId: string; fileName: string };

const { MenuItem } = Select;

const DEFAULT_EXPORT_WIDTH = 1600;
const DEFAULT_EXPORT_HEIGHT = 800;
const MIN_BAR_WIDTH = 4;

const FILE_TYPE_OPTIONS: { value: ChartExportFormat; label: string }[] = [
  { value: 'png', label: 'PNG' },
  { value: 'svg', label: 'SVG' },
];

const getDefaultWidth = (spec: VegaLiteSpec): number => {
  const dataPoints = spec.data?.values?.length ?? 0;
  return Math.max(DEFAULT_EXPORT_WIDTH, dataPoints * MIN_BAR_WIDTH);
};

const getSpecTitle = (spec: VegaLiteSpec): string => {
  if (!spec.title) {
    return '';
  }

  return typeof spec.title === 'string' ? spec.title : spec.title.text || '';
};

const styles: Record<string, CSSProperties> = {
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  row: {
    display: 'flex',
    gap: '1rem',
  },
  checkboxRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
};

const ChatExportChart: FC<ChatExportChartProps> = ({ spec, open, onClose }) => {
  const {
    ui: { setLoading },
  } = useChatContext();

  const [config, setConfig] = useState<ChartExportConfig>({
    format: 'png',
    title: '',
    xAxisTitle: '',
    yAxisTitle: '',
    width: getDefaultWidth(spec),
    height: DEFAULT_EXPORT_HEIGHT,
    includeValueLabels: false,
  });

  const updateConfig = (args: Partial<ChartExportConfig>) => {
    setConfig(current => ({ ...current, ...args }));
  };

  useEffect(() => {
    if (open) {
      updateConfig({
        format: 'png',
        title: getSpecTitle(spec),
        xAxisTitle: getChannelTitle(spec.encoding?.x) || '',
        yAxisTitle: getChannelTitle(spec.encoding?.y) || '',
        width: getDefaultWidth(spec),
        height: spec.height || DEFAULT_EXPORT_HEIGHT,
        includeValueLabels: false,
      });
    }
  }, [open]);

  const handleExport = async () => {
    onClose();
    setLoading(true);

    try {
      const { data } = await api.post<ChartExportResponse>(
        CHART_EXPORT_ENDPOINT,
        {
          spec,
          output: config.format,
          title: config.title || undefined,
          width: config.width,
          height: config.height,
          includeValueLabels: config.includeValueLabels,
          axisTitles: { x: config.xAxisTitle, y: config.yAxisTitle },
        },
      );

      downloadFile(`${FILES_ENDPOINT}/${data.fileId}`, data.fileName);
    } catch (error) {
      Snackbar.error({
        message: 'An unknown error occurred. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  const {
    format,
    title,
    xAxisTitle,
    yAxisTitle,
    width,
    height,
    includeValueLabels,
  } = config;

  return (
    <Modal
      title="Export Chart"
      open={open}
      onCancel={onClose}
      onConfirm={handleExport}
      confirmButtonText="Export"
    >
      <div style={styles.form}>
        <Select
          label="File Type"
          value={format}
          onChange={event => updateConfig({ format: event.target.value })}
          fullWidth
        >
          {FILE_TYPE_OPTIONS.map(option => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
        <TextField
          label="Title"
          value={title}
          onChange={event => updateConfig({ title: event.target.value })}
          fullWidth
        />
        <TextField
          label="X Axis Title"
          value={xAxisTitle}
          onChange={event => updateConfig({ xAxisTitle: event.target.value })}
          fullWidth
        />
        <TextField
          label="Y Axis Title"
          value={yAxisTitle}
          onChange={event => updateConfig({ yAxisTitle: event.target.value })}
          fullWidth
        />
        <div style={styles.row}>
          <TextField
            label="Width"
            type="number"
            value={width}
            onChange={event => updateConfig({ width: +event.target.value })}
            fullWidth
          />
          <TextField
            label="Height"
            type="number"
            value={height}
            onChange={event => updateConfig({ height: +event.target.value })}
            fullWidth
          />
        </div>
        <div style={styles.checkboxRow}>
          <Checkbox
            style={{ padding: 0 }}
            checked={includeValueLabels}
            onChange={() =>
              updateConfig({ includeValueLabels: !includeValueLabels })
            }
          />
          <span>Include labels for values</span>
        </div>
      </div>
    </Modal>
  );
};

export default ChatExportChart;
